/** Tour naming (plan §3.3.2, owner decision "Tour naming"): `tour.name` and
 *  `tour.name_template` edited together, with a live preview rendered SERVER-side against the
 *  product's own options (`name_preview/`, debounced) — exactly the renderer a sync uses, so
 *  what the preview shows is what the tours will be called. */
import { Alert, Box, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import * as catalogueApi from '@/api/catalogue';
import type { CatalogueOption, SettingSpec } from '@/api/catalogue';

import { needsTemplate } from './catalogueModel';
import { SettingControl } from './SettingControl';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export const NAME_PREVIEW_DEBOUNCE_MS = 400;

export function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export interface NameControlProps {
  tenantId: number;
  productId: number;
  modeSpec: SettingSpec;
  templateSpec: SettingSpec | undefined;
  mode: unknown;
  template: unknown;
  grain: unknown;
  options: readonly CatalogueOption[];
  onModeChange: (v: unknown) => void;
  onTemplateChange: (v: unknown) => void;
  modeError?: string | null;
  templateError?: string | null;
  disabled?: boolean;
}

export function NameControl({
  tenantId,
  productId,
  modeSpec,
  templateSpec,
  mode,
  template,
  grain,
  options,
  onModeChange,
  onTemplateChange,
  modeError,
  templateError,
  disabled,
}: NameControlProps) {
  const showTemplate = needsTemplate(mode) && templateSpec;
  const candidate = useDebouncedValue(
    {
      mode: typeof mode === 'string' ? mode : null,
      template: typeof template === 'string' ? template : null,
      grain: typeof grain === 'string' ? grain : null,
    },
    NAME_PREVIEW_DEBOUNCE_MS,
  );
  const preview = useQuery({
    queryKey: ['catalogue', tenantId, 'namePreview', productId, candidate],
    queryFn: ({ signal }) => catalogueApi.namePreview(tenantId, productId, candidate, signal),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const templateMissing = showTemplate && !(typeof template === 'string' && template.trim());

  return (
    <Stack spacing={1}>
      <SettingControl spec={modeSpec} value={mode} onChange={onModeChange} error={modeError} disabled={disabled} />
      {showTemplate && (
        <SettingControl
          spec={templateSpec}
          value={template}
          onChange={onTemplateChange}
          label="Template"
          error={templateError ?? (templateMissing ? 'A template is required in template mode.' : null)}
          disabled={disabled}
        />
      )}
      {showTemplate && templateSpec?.help && (
        <Typography variant="caption" color="text.secondary">
          {templateSpec.help}
        </Typography>
      )}
      <Box aria-live="polite">
        <Typography variant="caption" color="text.secondary">
          Preview {preview.isFetching ? '(updating…)' : ''}
        </Typography>
        {preview.isError && (
          <Alert severity="error" sx={{ py: 0 }}>
            {(preview.error as Error).message}
          </Alert>
        )}
        {preview.data && (
          <>
            {preview.data.names.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No tour: this grain builds none.
              </Typography>
            ) : (
              <Table size="small" sx={{ '& td': { py: 0.25, px: 1, borderBottom: 0 } }}>
                <TableBody>
                  {preview.data.names.map((n) => {
                    const opt = options.find((o) => o.id === n.option_id);
                    return (
                      <TableRow key={`${n.key}-${n.option_id}`}>
                        <TableCell sx={{ fontFamily: MONO, fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {n.key}
                        </TableCell>
                        <TableCell>
                          {n.error ? (
                            <Typography variant="body2" color="error">
                              {n.error.detail}
                            </Typography>
                          ) : (
                            <Typography variant="body2">{n.name}</Typography>
                          )}
                          {opt && opt.title && grain === 'option' && (
                            <Typography variant="caption" color="text.secondary">
                              option: {opt.title}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            {preview.data.errors.length > 0 && (
              <Alert severity="warning" sx={{ mt: 0.5, py: 0 }}>
                Saving would be refused: {preview.data.errors.map((e) => e.detail).join(' ')}
              </Alert>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
}
